import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Music, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import Map from '@/components/maps/Map';
import DisplayBreadcrumbs from '@/components/displays/DisplayBreadcrumbs';
import DisplayHeader from '@/components/displays/DisplayHeader';
import DisplayDescription from '@/components/displays/DisplayDescription';
import DisplayScheduleCard from '@/components/displays/DisplayScheduleCard';
import DisplayDetailsCard from '@/components/displays/DisplayDetailsCard';
import DisplayOwnerCard from '@/components/displays/DisplayOwnerCard';
import DisplaySequencesCard from '@/components/displays/DisplaySequencesCard';
import DisplayHistoryCard from '@/components/displays/DisplayHistoryCard';
import CharityCard from '@/components/charity/CharityCard';
import { useCharity } from '@/hooks/useCharity';
import { useDisplay } from '@/hooks/useDisplays';
import { useRatingStats } from '@/hooks/useRatingStats';
import { DisplayYear } from '@/types/displayHistory';
import { Display } from '@/types/display';
import { fetchDisplayYears } from '@/utils/displayYearsUtils';
import { supabase } from '@/integrations/supabase/client';
import ReviewSection from '@/components/reviews/ReviewSection';
import RatingSummary from '@/components/reviews/RatingSummary';
import RecentReviewsHighlight from '@/components/reviews/RecentReviewsHighlight';
import DisplaySeoFooter from '@/components/displays/DisplaySeoFooter';
import { 
  SchemaMarkup, 
  createLocalBusinessSchema, 
  createEventSchema, 
  createBreadcrumbSchema 
} from '@/components/seo/SchemaMarkup';
import { getSeoLocationComponents } from '@/lib/locationParser';
import SmartUpvoteButton from '@/components/shared/SmartUpvoteButton';
import { useAuth } from '@/contexts/AuthContext';
import PhotoGallery from '@/components/displays/PhotoGallery';

const DisplayDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const parsedId = id ? parseInt(id, 10) : undefined;
  const { data: display, isLoading: isLoadingDisplay, error: displayError } = useDisplay(parsedId);
  const [displayYears, setDisplayYears] = useState<DisplayYear[]>([]);
  const [isFavorite, setIsFavorite] = useState(false);
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [songCount, setSongCount] = useState(0);
  const { charity, isLoading: isLoadingCharity } = useCharity(ownerId);
  const { stats: ratingStats, isLoading: isLoadingRating } = useRatingStats('display', display?.id || '');
  
  // Admin check from auth context (email-gated admin)
  const { user } = useAuth();
  const isAdmin = user?.email === 'joey@towb.in';
  
  useEffect(() => {
    const loadDisplayData = async () => {
      if (!display?.id) return;
      
      try {
        // Fetch real display years from database
        const years = await fetchDisplayYears(display.id);
        console.log('Fetched display years:', years);
        setDisplayYears(years);
        
        // Set real owner ID from display data
        if (display.user_id) {
          setOwnerId(display.user_id);
        }
        
        // Calculate real song count from years data
        let totalSongs = 0;
        for (const year of years) {
          const { data: songs } = await supabase
            .from('display_songs')
            .select('id')
            .eq('display_year_id', year.id);
          totalSongs += songs?.length || 0;
        }
        setSongCount(totalSongs);
        console.log('Total songs found:', totalSongs);
      } catch (error) {
        console.error('Error loading display data:', error);
        // Fallback to empty arrays on error
        setDisplayYears([]);
        setSongCount(0);
      }
    };
    
    loadDisplayData();
  }, [display]);

  // Helper function to parse address from location string
  const parseAddress = (location: string) => {
    const parts = location.split(',').map(part => part.trim());
    const addressLocality = parts[0] || '';
    const addressRegion = parts[1] || '';
    const postalCode = parts[2] || '';
    
    return {
      addressLocality,
      addressRegion,
      postalCode,
      addressCountry: 'US'
    };
  };

  // Helper function to generate holiday-specific dates
  const getHolidayDates = (holidays: string[]) => {
    const currentYear = new Date().getFullYear();
    
    // Default to Christmas season if no specific holiday
    if (!holidays || holidays.length === 0) {
      return {
        startDate: `${currentYear}-12-01T17:00:00`,
        endDate: `${currentYear}-12-31T22:00:00`
      };
    }

    const holiday = holidays[0].toLowerCase();
    switch (holiday) {
      case 'christmas':
        return {
          startDate: `${currentYear}-12-01T17:00:00`,
          endDate: `${currentYear}-12-31T22:00:00`
        };
      case 'halloween':
        return {
          startDate: `${currentYear}-10-01T18:00:00`,
          endDate: `${currentYear}-10-31T21:00:00`
        };
      case '4th of july':
      case 'independence day':
        return {
          startDate: `${currentYear}-07-01T20:00:00`,
          endDate: `${currentYear}-07-04T23:00:00`
        };
      default:
        return {
          startDate: `${currentYear}-12-01T17:00:00`,
          endDate: `${currentYear}-12-31T22:00:00`
        };
    }
  };

  // Generate schema markup if display is loaded
  const generateSchemaMarkup = () => {
    if (!display || !display.latitude || !display.longitude) return null;

    const address = parseAddress(display.location || '');
    const { startDate, endDate } = getHolidayDates(display.holidays);
    const currentUrl = window.location.href;

    // Local Business Schema
    const localBusinessSchema = createLocalBusinessSchema(
      display.name,
      display.description || `Holiday light display featuring ${display.holidays?.join(', ') || 'Christmas'} lights`,
      address,
      {
        latitude: display.latitude,
        longitude: display.longitude
      },
      currentUrl,
      undefined, // telephone
      display.schedule ? [`Mo-Su ${display.schedule}`] : undefined,
      'Free' // Most displays are free to view
    );

    // Event Schema
    const eventSchema = createEventSchema(
      `${display.name} - Holiday Light Display`,
      display.description || `Experience the magic of ${display.holidays?.join(', ') || 'Christmas'} at this spectacular holiday light display featuring synchronized music and stunning decorations.`,
      startDate,
      endDate,
      {
        name: display.name,
        address,
        geo: {
          latitude: display.latitude,
          longitude: display.longitude
        }
      },
      {
        name: display.owner?.name || 'Display Owner',
        url: display.website
      },
      currentUrl,
      display.image_url,
      {
        price: '0',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: currentUrl
      }
    );

    // Breadcrumb Schema
    const breadcrumbSchema = createBreadcrumbSchema([
      { name: 'Home', url: window.location.origin },
      { name: 'Displays', url: `${window.location.origin}/displays` },
      { name: display.name, url: currentUrl }
    ]);

    return { localBusinessSchema, eventSchema, breadcrumbSchema };
  };

  const schemas = generateSchemaMarkup();
  
  const isLoading = isLoadingDisplay;
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <NavBar />
        <main className="grow flex items-center justify-center">
          <div className="animate-pulse text-xl">Loading display details...</div>
        </main>
      </div>
    );
  }
  
  if (displayError || !display) {
    return (
      <div className="min-h-screen flex flex-col">
        <NavBar />
        <main className="grow flex flex-col items-center justify-center p-6">
          <div className="text-xl mb-4">Display not found</div>
          <p className="text-muted-foreground mb-4">
            {displayError ? (displayError as Error).message : "The requested display could not be found."}
          </p>
          <Link to="/displays">
            <Button className="mt-4">
              <ArrowLeft className="mr-2" size={16} />
              Browse Displays
            </Button>
          </Link>
        </main>
      </div>
    );
  }

  // Generate page title and meta description
  const pageTitle = `${display.name} - Holiday Light Display in ${display.location} | LightsHunt`;
  const metaDescription = `Visit ${display.name} in ${display.location} for an amazing ${display.holidays?.join(', ') || 'holiday'} light display. ${display.description || 'Experience spectacular synchronized lights and music.'} Find directions, schedules, and reviews.`;
  const locationKeywords = display.location ? display.location.split(',').map(part => part.trim()).join(', ') : '';
  const holidayKeywords = display.holidays?.join(', ') || 'Christmas, holiday lights';
  const keywords = `holiday lights, ${holidayKeywords}, light display, ${locationKeywords}, Christmas decorations, synchronized lights, ${display.name}`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={keywords} />
        
        {/* Open Graph tags */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="place" />
        <meta property="og:url" content={window.location.href} />
        {display.image_url && <meta property="og:image" content={display.image_url} />}
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        {display.image_url && <meta name="twitter:image" content={display.image_url} />}
        
        {/* Local business specific meta tags */}
        <meta name="geo.region" content={display.location?.split(',')[1]?.trim()} />
        <meta name="geo.placename" content={display.location?.split(',')[0]?.trim()} />
        {display.latitude && <meta name="geo.position" content={`${display.latitude};${display.longitude}`} />}
        <meta name="ICBM" content={`${display.latitude}, ${display.longitude}`} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      {/* Schema Markup */}
      {schemas && (
        <>
          <SchemaMarkup schema={schemas.localBusinessSchema} />
          <SchemaMarkup schema={schemas.eventSchema} />
          <SchemaMarkup schema={schemas.breadcrumbSchema} />
        </>
      )}

      <div className="min-h-screen flex flex-col">
        <NavBar />
        
        <main className="grow pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-6">
            {/* Breadcrumbs */}
            <DisplayBreadcrumbs displayName={display.name} />
            
            {/* Admin quick actions */}
            {isAdmin && display?.id && (
              <div className="flex justify-end mb-4">
                <Button
                  variant="outline"
                  onClick={() => (window.location.href = `/admin?display=${display.id}`)}
                  className="flex items-center gap-2"
                >
                  <Shield className="h-4 w-4" />
                  Edit in Admin
                </Button>
              </div>
            )}

            {/* Main content grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left column - Display images and details */}
              <div className="lg:col-span-2 space-y-8">
                {/* Photo gallery (supports multiple photos, falls back to image_url) */}
                <div className="rounded-xl overflow-hidden">
                  {Array.isArray((display as any).photos) && (display as any).photos.length > 0 ? (
                    <PhotoGallery
                      photos={(display as any).photos as string[]}
                      displayName={display.name}
                    />
                  ) : (
                    <img
                      src={display.image_url || ''}
                      alt={display.name}
                      className="w-full h-auto aspect-video object-cover"
                    />
                  )}
                </div>
                
                {/* Display info */}
                <div>
                  <DisplayHeader 
                    name={display.name}
                    displayType={display.display_type || ''}
                    ratingStats={ratingStats}
                    holidays={display.holidays || []}
                    location={display.location}
                    isFavorite={isFavorite}
                    onToggleFavorite={() => setIsFavorite(!isFavorite)}
                  />
                  <div className="mt-3">
                    <SmartUpvoteButton
                      target="display"
                      id={display.id}
                      holidayType={display.holiday_type}
                      className=""
                      onRequireAuth={() => {
                        // For displays, reuse the same sign-in trigger behavior as favorites
                        // This app uses central auth components; opening any auth dialog works
                        // The Display page does not currently have a dedicated modal hook,
                        // so we navigate to /auth which shows the sign-in dialog.
                        window.location.href = '/auth';
                      }}
                    />
                  </div>
                  
                  <DisplayDescription 
                    description={display.description || ''}
                    tags={display.tags || []}
                  />
                  
                  {/* Rating Summary and Recent Reviews */}
                  {ratingStats && ratingStats.rating_count > 0 && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
                      <RatingSummary 
                        stats={ratingStats}
                        showDistribution={true}
                        showPercentages={true}
                      />
                      <RecentReviewsHighlight
                        targetType="display"
                        targetId={display.id}
                        maxReviews={3}
                        showTitle={true}
                      />
                    </div>
                  )}
                  
                  {/* Display details grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                    {/* Schedule */}
                    <DisplayScheduleCard 
                      schedule={display.schedule} 
                      city={display.city}
                      state={display.state}
                    />
                    
                    {/* Additional Info */}
                    <DisplayDetailsCard 
                      yearStarted={display.year_started}
                      fmStation={display.fm_station || null}
                      radioStation={display.radio_station || null}
                      parkingInfo={display.parking_other || null}
                      songCount={songCount}
                    />
                  </div>
                </div>
                
                {/* Display Owner Section moved to right column */}
                
                {/* Display History Section */}
                <div className="mt-8">
                  <h2 className="text-2xl font-semibold mb-4">Display History</h2>
                  <DisplayHistoryCard 
                    displayId={parseInt(display.id)}
                    years={displayYears}
                  />
                </div>

                {/* Reviews Section */}
                <div className="mt-8">
                  <ReviewSection
                    targetType="display"
                    targetId={display.id}
                    targetName={display.name}
                  />
                </div>
              </div>
              
              {/* Right column - Map, Owner, Related, Charity */}
              <div className="space-y-6">
                {/* Display Owner (top-right) */}
                <div>
                  <h2 className="text-2xl font-semibold mb-4">Display Owner</h2>
                  <DisplayOwnerCard 
                    avatarUrl={display.owner?.avatar || "https://i.pravatar.cc/150?img=1"}
                    ownerName={display.owner?.name || "John Doe"}
                    ownerId={display.owner?.id || '1'}
                    facebook={display.facebook}
                    website={display.website}
                  />
                </div>
                {/* Map with Mapbox */}
                {display.latitude && display.longitude && (
                  <div className="card-wrapper">
                    <Map 
                      latitude={display.latitude} 
                      longitude={display.longitude}
                      markerTitle={display.name}
                      style="dark"
                    />
                    <div className="mt-4 text-sm">
                      <div className="font-medium">{display.location}</div>
                      {display.radio_station && (
                        <div className="text-muted-foreground mt-1 flex items-center">
                          <Music className="h-3.5 w-3.5 mr-1" />
                          <span>Tune your radio to {display.radio_station} FM for the show soundtrack.</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Display owner card moved to left column */}
                
                {/* Display charity card if available */}
                {charity && !isLoadingCharity && (
                  <CharityCard charity={charity} variant="compact" />
                )}
                
                {/* Recent Reviews Sidebar */}
                <RecentReviewsHighlight
                  targetType="display"
                  targetId={display.id}
                  maxReviews={2}
                  showTitle={true}
                  className="lg:block hidden"
                />
                
                {/* Related sequences card */}
                <DisplaySequencesCard displayId={parseInt(display.id)} isAdmin={isAdmin} />
              </div>
            </div>
          </div>
        </main>
        
        {/* SEO Footer */}
        <DisplaySeoFooter
          city={display.location?.split(',')[0]?.trim() || ''}
          state={display.location?.split(',')[1]?.trim() || ''}
          zip={''}
          latitude={display.latitude || 0}
          longitude={display.longitude || 0}
          displayId={display.id}
          location={display.location}
        />
        <Footer />
      </div>
    </>
  );
};

export default DisplayDetail;
